import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Spin,
  Tabs,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Table,
  Modal,
  Popconfirm,
  message,
  Divider,
  Row,
  Col,
  DatePicker,
} from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  DeleteOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useStore } from '../../stores/root-store-context';
import { Contact, ExternalCompany, CrmEvent } from '../../api/CRMApiClient';
import ERPLayout from '../../components/layout/ERPLayout';

dayjs.extend(utc);
dayjs.extend(timezone);

const toLocal = (iso: string) => dayjs(iso).local();
const toApi = (d: Dayjs) => d.utc().format('YYYY-MM-DDTHH:mm:ss');

const { TabPane } = Tabs;
const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const ExternalCompanyDetail: React.FC = observer(() => {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId?: string }>();
  const navigate = useNavigate();
  const { crmStore, applicationStore } = useStore();

  const isCreateMode = !companyId || companyId === 'new';
  const companyIdNumber = useMemo(
    () => (!isCreateMode && !isNaN(Number(companyId)) ? Number(companyId) : null),
    [isCreateMode, companyId],
  );

  const [form] = Form.useForm<ExternalCompany>();
  const [editing, setEditing] = useState<boolean>(isCreateMode);
  const [loading, setLoading] = useState<boolean>(true);

  const [contactModalOpen, setContactModalOpen] = useState<boolean>(false);
  const [contactForm] = Form.useForm();
  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  const [eventModalVisible, setEventModalVisible] = useState<boolean>(false);
  const [eventForm] = Form.useForm();
  const [editingEvent, setEditingEvent] = useState<CrmEvent | null>(null);

  const [relatedContacts, setRelatedContacts] = useState<Contact[]>([]);
  const [relatedEvents, setRelatedEvents] = useState<any[]>([]);

  const buildCompositeName = useCallback(
    (companyIds?: number[], contactIds?: number[], range?: [Dayjs, Dayjs]) => {
      const parts: string[] = [];
      if (companyIds?.length) {
        const first = crmStore.externalCompanies.find(c => c.id === companyIds[0]);
        if (first) parts.push(first.name);
        if (companyIds.length > 1) parts.push(`+${companyIds.length - 1}`);
      }
      if (contactIds?.length) {
        const first = crmStore.contacts.find(c => c.id === contactIds[0]);
        if (first) parts.push(`${first.name} ${first.surname}`.trim());
        if (contactIds.length > 1) parts.push(`+${contactIds.length - 1}`);
      }
      if (range?.[0]) parts.push(range[0].format('DD/MM/YYYY'));
      return parts.filter(Boolean).join(' — ');
    },
    [crmStore.externalCompanies, crmStore.contacts],
  );

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        crmStore.getClientRoles(),
        crmStore.getCrmEventTypes(),
        crmStore.getContactsWithDetails(),
      ]);

      if (!isCreateMode && companyIdNumber !== null) {
        const company = await crmStore.ApiStore.CRMApiClient.getExternalCompanyById(
          companyIdNumber,
        );
        if (company) {
          form.setFieldsValue(company);
          const contacts = await crmStore.getContacts();
          setRelatedContacts(contacts.filter(c => c.externalCompanyId === companyIdNumber));
          const events = await crmStore.ApiStore.CRMApiClient.getCrmEventsByExternalCompany(
            companyIdNumber,
          );
          setRelatedEvents(events);
        }
      } else {
        form.setFieldsValue({ name: '', edrpou: '', description: '', addresses: [] });
      }
      setLoading(false);
    };
    loadData();
  }, [crmStore, isCreateMode, companyIdNumber, form]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      if (isCreateMode) {
        const created = await crmStore.addExternalCompany(values);
        message.success(t('CreatedSuccessfully'));
        navigate(`/${applicationStore.company?.Tenant}/crm/company/${created?.id}`);
      } else if (companyIdNumber !== null) {
        await crmStore.updateExternalCompany(companyIdNumber, values);
        setEditing(false);
        message.success(t('SavedSuccessfully'));
      }
    } catch {}
  };

  const onDelete = async () => {
    if (companyIdNumber === null) return;
    await crmStore.deleteExternalCompany(companyIdNumber);
    navigate(`/${applicationStore.company?.Tenant}/crm/companies`);
  };

  const refreshContacts = async () => {
    if (companyIdNumber === null) return;
    const contacts = await crmStore.getContacts();
    setRelatedContacts(contacts.filter(c => c.externalCompanyId === companyIdNumber));
  };

  const openAddContact = () => {
    if (companyIdNumber === null) return;
    setEditingContact(null);
    contactForm.resetFields();
    contactForm.setFieldsValue({
      externalCompanyId: companyIdNumber,
      additionalInfos: [],
      name: '',
      surname: '',
      email: '',
      phoneNumber: '',
      roleId: null,
    });
    setContactModalOpen(true);
  };

  const openEditContact = (c: Contact) => {
    setEditingContact(c);
    contactForm.setFieldsValue({ ...c, additionalInfos: c.additionalInfos || [] });
    setContactModalOpen(true);
  };

  const saveContact = async () => {
    const vals = await contactForm.validateFields();
    if (editingContact) {
      await crmStore.updateContact(editingContact.id, vals);
    } else {
      await crmStore.addContact(vals);
    }
    setContactModalOpen(false);
    refreshContacts();
    message.success(editingContact ? t('SavedSuccessfully') : t('CreatedSuccessfully'));
  };

  const deleteContact = async (id: number) => {
    await crmStore.deleteContact(id);
    refreshContacts();
    message.success(t('DeletedSuccessfully'));
  };

  const refreshEvents = async () => {
    if (companyIdNumber === null) return;
    const events = await crmStore.ApiStore.CRMApiClient.getCrmEventsByExternalCompany(
      companyIdNumber,
    );
    setRelatedEvents(events);
  };

  const openAddEvent = () => {
    if (companyIdNumber === null) return;
    setEditingEvent(null);
    const now = dayjs();
    eventForm.setFieldsValue({
      dateRange: [now, now.clone().add(30, 'minutes')],
      externalCompanyIds: [companyIdNumber],
      contactIds: [],
      name: '',
      description: '',
      typeId: null,
    });
    setEventModalVisible(true);
  };

  const openEditEvent = (event: CrmEvent) => {
    setEditingEvent(event);
    eventForm.setFieldsValue({
      name: event.name,
      description: event.description,
      typeId: event.typeId,
      dateRange: [toLocal(event.dateStart), toLocal(event.dateEnd)],
      externalCompanyIds: event.externalCompanies?.map(c => c.id) ?? [],
      contactIds: event.contacts?.map(c => c.id) ?? [],
    });
    setEventModalVisible(true);
  };

  const handleEventValuesChange = (_: any, all: any) => {
    const composite = buildCompositeName(all.externalCompanyIds, all.contactIds, all.dateRange);
    if (composite && composite !== all.name) {
      eventForm.setFieldsValue({ name: composite });
    }
  };

  const saveEvent = async () => {
    const { dateRange, externalCompanyIds, contactIds, ...rest } =
      await eventForm.validateFields();
    const payload = {
      ...rest,
      dateStart: toApi(dateRange[0]),
      dateEnd: toApi(dateRange[1]),
      externalCompanyIds: externalCompanyIds || [],
      contactIds: contactIds || [],
      name: buildCompositeName(externalCompanyIds, contactIds, dateRange),
    };
    if (editingEvent) {
      await crmStore.updateCrmEvent(editingEvent.id, payload);
    } else {
      await crmStore.addCrmEvent(payload);
    }
    setEventModalVisible(false);
    refreshEvents();
    message.success(editingEvent ? t('SavedSuccessfully') : t('CreatedSuccessfully'));
  };

  const deleteEvent = async (id: number) => {
    await crmStore.deleteCrmEvent(id);
    refreshEvents();
    message.success(t('DeletedSuccessfully'));
  };

  const contactColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: '5%' },
    { title: t('Name'), dataIndex: 'name', key: 'name', width: '20%' },
    { title: t('Surname'), dataIndex: 'surname', key: 'surname', width: '20%' },
    { title: t('Email'), dataIndex: 'email', key: 'email', width: '20%' },
    { title: t('Phone'), dataIndex: 'phoneNumber', key: 'phoneNumber', width: '15%' },
    {
      title: t('Role'),
      dataIndex: 'roleId',
      key: 'roleId',
      width: '10%',
      render: (id: number) => crmStore.clientRoles.find(r => r.id === id)?.name,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: '10%',
      render: (_: unknown, record: Contact) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEditContact(record)} />
          <Popconfirm
            title={t('DeleteContactConfirm')}
            onConfirm={() => deleteContact(record.id)}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const eventColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: '5%' },
    { title: t('Name'), dataIndex: 'name', key: 'name', width: '25%' },
    {
      title: t('Start'),
      dataIndex: 'dateStart',
      key: 'dateStart',
      width: '15%',
      render: (d: string) => dayjs(d).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: t('End'),
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      width: '15%',
      render: (d: string) => dayjs(d).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: t('EventType'),
      dataIndex: 'typeId',
      key: 'typeId',
      width: '15%',
      render: (tid: number) => crmStore.CrmEventTypes.find(t => t.id === tid)?.name,
    },
    {
      title: t('Contacts'),
      key: 'contacts',
      width: '15%',
      render: (_: unknown, r: any) => {
        const cts = r.contacts || [];
        if (cts.length === 0) return '';
        if (cts.length === 1) return `${cts[0].name} ${cts[0].surname}`;
        return `${cts[0].name} ${cts[0].surname} +${cts.length - 1}`;
      },
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: '10%',
      render: (_: unknown, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEditEvent(record)} />
          <Popconfirm
            title={t('DeleteEventConfirm')}
            onConfirm={() => deleteEvent(record.id)}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const breadcrumbItems = [
    { label: t('Home'), path: `/${applicationStore.company?.Tenant}` },
    { label: 'CRM', path: `/${applicationStore.company?.Tenant}/crm` },
    { label: t('Companies'), path: `/${applicationStore.company?.Tenant}/crm/companies` },
    {
      label: isCreateMode ? t('NewCompany') : t('CompanyDetails'),
      path: isCreateMode
        ? `/${applicationStore.company?.Tenant}/crm/company/new`
        : `/${applicationStore.company?.Tenant}/crm/company/${companyIdNumber}`,
    },
  ];

  return (
    <ERPLayout breadcrumbTitle={t('CompanyDetails')} breadcrumbItems={breadcrumbItems}>
      <Spin spinning={loading}>
        <Card
          title={
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/companies`)}
              />
              <Title level={4} style={{ margin: 0 }}>
                {isCreateMode
                  ? t('NewCompany')
                  : crmStore.externalCompanies.find(c => c.id === companyIdNumber)?.name ||
                    t('CompanyDetails')}
              </Title>
            </Space>
          }
          extra={
            <Space>
              {editing ? (
                <>
                  <Button type="primary" icon={<SaveOutlined />} onClick={onSave}>
                    {t('Save')}
                  </Button>
                  {!isCreateMode && <Button onClick={() => setEditing(false)}>{t('Cancel')}</Button>}
                </>
              ) : (
                <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
                  {t('Edit')}
                </Button>
              )}
              {!isCreateMode && (
                <Popconfirm title={t('DeleteCompanyConfirm')} onConfirm={onDelete}>
                  <Button danger icon={<DeleteOutlined />}>
                    {t('Delete')}
                  </Button>
                </Popconfirm>
              )}
            </Space>
          }
        >
          <Tabs defaultActiveKey="details">
            <TabPane tab={t('Details')} key="details">
              <Form form={form} layout="vertical" disabled={!editing}>
                <Form.Item
                  name="name"
                  label={t('Name')}
                  rules={[{ required: true, message: t('PleaseEnterName') }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item name="edrpou" label={t('EDRPOU')}>
                  <Input />
                </Form.Item>
                <Form.Item name="description" label={t('Description')}>
                  <TextArea rows={3} />
                </Form.Item>

                <Divider orientation="left">{t('Addresses')}</Divider>
                <Form.List name="addresses">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...rest }) => (
                        <Row gutter={12} key={key} align="middle">
                          <Col span={6}>
                            <Form.Item {...rest} name={[name, 'locationName']}>
                              <Input placeholder={t('LocationName')} />
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item {...rest} name={[name, 'city']}>
                              <Input placeholder={t('City')} />
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item {...rest} name={[name, 'country']}>
                              <Input placeholder={t('Country')} />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <MinusCircleOutlined onClick={() => remove(name)} />
                          </Col>
                        </Row>
                      ))}
                      <Form.Item>
                        <Button
                          type="dashed"
                          block
                          icon={<PlusCircleOutlined />}
                          onClick={() => add()}
                        >
                          {t('AddAddress')}
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Form>
            </TabPane>

            {!isCreateMode && (
              <>
                <TabPane tab={t('Contacts')} key="contacts">
                  <div style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAddContact}>
                      {t('AddContact')}
                    </Button>
                  </div>
                  <Table
                    dataSource={relatedContacts}
                    columns={contactColumns}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    onRow={rec => ({
                      onDoubleClick: () =>
                        navigate(`/${applicationStore.company?.Tenant}/crm/contact/${rec.id}`),
                    })}
                  />
                </TabPane>

                <TabPane tab={t('Events')} key="events">
                  <div style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAddEvent}>
                      {t('AddEvent')}
                    </Button>
                  </div>
                  <Table
                    dataSource={relatedEvents}
                    columns={eventColumns}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    expandable={{
                      expandedRowRender: rec => (
                        <div style={{ padding: 16 }}>
                          <Text strong>{t('Description')}:</Text>
                          <Typography.Paragraph>
                            {rec.description || t('NoDescription')}
                          </Typography.Paragraph>
                        </div>
                      ),
                    }}
                    onRow={rec => ({
                      onDoubleClick: () =>
                        navigate(`/${applicationStore.company?.Tenant}/crm/event/${rec.id}`),
                    })}
                  />
                </TabPane>
              </>
            )}
          </Tabs>
        </Card>

        <Modal
          title={editingContact ? t('EditContact') : t('AddContact')}
          open={contactModalOpen}
          onOk={saveContact}
          onCancel={() => setContactModalOpen(false)}
          width={800}
          destroyOnClose
          okText={t('OK')}
          cancelText={t('Cancel')}
        >
          <Form form={contactForm} layout="vertical">
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item name="roleId" label={t('Role')}>
                  <Select allowClear>
                    {crmStore.clientRoles.map(r => (
                      <Option key={r.id} value={r.id}>
                        {r.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="name"
                  label={t('Name')}
                  rules={[{ required: true, message: t('PleaseEnterName') }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="surname"
                  label={t('Surname')}
                  rules={[{ required: true, message: t('PleaseEnterSurname') }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item name="email" label={t('Email')}>
                  <Input type="email" />
                </Form.Item>
                <Form.Item name="phoneNumber" label={t('Phone')}>
                  <Input />
                </Form.Item>
                <Form.Item name="externalCompanyId" hidden>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Divider orientation="left">{t('AdditionalContactInfo')}</Divider>
                <Form.List name="additionalInfos">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Space key={key} align="baseline" style={{ marginBottom: 8 }}>
                          <Form.Item
                            {...restField}
                            name={[name, 'type']}
                            rules={[{ required: true, message: t('PleaseSelectType') }]}
                          >
                            <Select placeholder={t('Type')} style={{ width: 150 }}>
                              <Option value="Email">Email</Option>
                              <Option value="LinkedIn">LinkedIn</Option>
                              <Option value="Skype">Skype</Option>
                              <Option value="Telegram">Telegram</Option>
                              <Option value="Phone">Phone</Option>
                            </Select>
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, 'name']}
                            rules={[{ required: true, message: t('PleaseEnterValue') }]}
                          >
                            <Input placeholder={t('Value')} style={{ width: 200 }} />
                          </Form.Item>
                          <MinusCircleOutlined onClick={() => remove(name)} />
                        </Space>
                      ))}
                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          block
                          icon={<PlusCircleOutlined />}
                        >
                          {t('AddField')}
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Col>
            </Row>
          </Form>
        </Modal>

        <Modal
          title={editingEvent ? t('EditEvent') : t('AddEvent')}
          open={eventModalVisible}
          onOk={saveEvent}
          onCancel={() => setEventModalVisible(false)}
          width={800}
          destroyOnClose
          okText={t('OK')}
          cancelText={t('Cancel')}
        >
          <Form form={eventForm} layout="vertical" onValuesChange={handleEventValuesChange}>
            <Form.Item name="externalCompanyIds" label={t('Companies')}>
              <Select mode="multiple" allowClear showSearch optionFilterProp="children">
                {crmStore.externalCompanies.map(c => (
                  <Option key={c.id} value={c.id}>
                    {c.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="contactIds" label={t('Contacts')}>
              <Select mode="multiple" allowClear showSearch optionFilterProp="children">
                {crmStore.contacts.map(c => (
                  <Option key={c.id} value={c.id}>
                    {c.name} {c.surname}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="dateRange"
              label={t('DateTime')}
              rules={[{ required: true, message: t('PleaseSelectDateTime') }]}
            >
              <RangePicker
                showTime
                allowClear={false}
                format="DD/MM/YYYY HH:mm"
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              name="typeId"
              label={t('EventType')}
              rules={[{ required: true, message: t('PleaseSelectEventType') }]}
            >
              <Select placeholder={t('Select')} allowClear>
                {crmStore.CrmEventTypes.map(type => (
                  <Option key={type.id} value={type.id}>
                    {type.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="name" label={t('Name')} rules={[{ required: true }]}>
              <Input disabled />
            </Form.Item>
            <Form.Item name="description" label={t('Description')}>
              <TextArea rows={4} />
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
    </ERPLayout>
  );
});

export default ExternalCompanyDetail;
